import { DateTime } from 'luxon';
import { UserStore } from '@/store/user-module';
import { FileResponse } from '@/services/api/nswag/stapiclient_generated';
import { stAccountApi } from '@/services/st-api';
import numeral from 'numeral';

export class ToggleHandler {
  private openIds: Array<number> = [];

  public toggleMultiple(ids: Array<number>) {
    ids.forEach((x) => this.toggle(x));
  }

  public toggle(id: number) {
    if (this.isOpen(id)) {
      this.openIds.splice(
        this.openIds.findIndex((x) => x == id),
        1
      );
    } else {
      this.openIds.push(id);
    }
  }

  isOpen(id: number) {
    return this.openIds.some((x) => x == id);
  }
}

export class ObjectEditor<T> {
  data: T | null = null;
  originalData: T | null = null;
  editMode = false;
  isNew = false;

  constructor(_data: T) {
    this.data = _data;
    this.originalData = JSON.parse(JSON.stringify(this.data));
  }

  get val() {
    return this.data!;
  }

  set val(data: T) {
    this.data = data;
  }

  reset() {
    this.data = JSON.parse(JSON.stringify(this.originalData));
    this.editMode = false;
  }
}

export async function getLogo(file: FileResponse) {
  let res = { logo: '', logoBase64: '' };
  if (!!file?.data?.size) {
    res.logo = URL.createObjectURL(file.data);

    const reader = new FileReader();
    reader.readAsDataURL(file.data);
    const base64Data = await new Promise<string>((resolve) => {
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
    });

    res.logoBase64 = base64Data;
  }

  return res;
}

export function dateEqual(dateTime: DateTime, dateTime2: DateTime) {
  return dateTime?.year === dateTime2?.year && dateTime?.month === dateTime2?.month && dateTime?.day === dateTime2?.day;
}

export function dateGreaterEqual(dateTime: DateTime, dateTime2: DateTime) {
  return dateTime?.year >= dateTime2?.year && dateTime?.month >= dateTime2?.month && dateTime?.day >= dateTime2?.day;
}

export function dateSmallerEqual(dateTime: DateTime, dateTime2: DateTime) {
  return dateTime?.year <= dateTime2?.year && dateTime?.month <= dateTime2?.month && dateTime?.day <= dateTime2?.day;
}

export function padZero(timeInt: number) {
  return timeInt < 10 ? '0' + timeInt.toString() : timeInt.toString();
}

export function getHours(durationMinutes: number, showPlusSign: boolean = false, showPlusMinusSign: boolean = false) {
  let hoursInt;
  let minutesInt;

  const isNegative = durationMinutes < 0;

  durationMinutes = Math.abs(durationMinutes);

  hoursInt = Math.floor(durationMinutes / 60);
  minutesInt = Math.abs(durationMinutes - hoursInt * 60);

  let sign = isNegative ? '-' : '';

  if (!isNegative && showPlusSign) {
    sign = '+';
  }

  if (!hoursInt && !minutesInt) {
    if (showPlusMinusSign) {
      sign = '±';
    } else {
      sign = '';
    }
  }

  return sign + padZero(hoursInt) + ':' + padZero(minutesInt);
}

export function getHoursFromSeconds(durationSeconds: number) {
  let hoursInt;
  let minutesInt;
  let secondsInt;

  hoursInt = Math.floor(durationSeconds / 60 / 60);
  minutesInt = Math.floor((durationSeconds - hoursInt * 60 * 60) / 60);
  secondsInt = Math.abs(durationSeconds - hoursInt * 60 * 60 - minutesInt * 60);

  return padZero(hoursInt) + ':' + padZero(minutesInt) + ':' + padZero(secondsInt);
}

export function nthIndex(str: string, pat: string, n: number) {
  let L = str.length,
    i = -1;
  while (n-- && i++ < L) {
    i = str.indexOf(pat, i);
    if (i < 0) {
      break;
    }
  }
  return i;
}

export function startOfMonth(date: DateTime) {
  return DateTime.fromFormat('1.' + date.toFormat('M.yyyy'), 'd.M.yyyy', { zone: 'utc' });
}

export function endOfMonth(date: DateTime) {
  const firstOfMonth = startOfMonth(date);

  return firstOfMonth.plus({ months: 1 }).minus({ days: 1 });
}

export function startOfWeek(date: DateTime) {
  let weekdayNumber = parseInt(date.toFormat('E'));
  weekdayNumber = weekdayNumber === 7 ? 0 : weekdayNumber;

  const startOfWeek = UserStore.userSettings?.firstDayOfWeekInt ?? 1;
  const diff = (7 + (weekdayNumber - startOfWeek)) % 7;
  return date.minus({ days: diff });
}

export function endOfWeek(date: DateTime) {
  return startOfWeek(date.plus({ days: 7 })).minus({ days: 1 });
}

export function DtFromIso(dateISO: string) {
  return DateTime.fromISO(dateISO, { zone: 'utc' });
}

export function GetElemByRef(ref: any) {
  if (ref.$el) {
    return ref.$el;
  }

  return ref;
}

export function DateForEach(dateFrom: DateTime, dateTo: DateTime) {
  let res: DateTime[] = [];
  for (let start = dateFrom; start <= dateTo; start = start.plus({ days: 1 })) {
    res.push(start);
  }

  return res;
}

export function clone(item: any) {
  return JSON.parse(JSON.stringify(item));
}

export function roundToTwo(num: number) {
  //@ts-ignore
  return +(Math.round(num + 'e+2') + 'e-2');
}

export function formatCentToEuro(value: number) {
  numeral.locale('de'); //TODO: usersettings!
  return numeral(value / 100).format('0,0.00');
}

export function formatDate(value: any, formatString: string = '') {
  if (!value) {
    return;
  }
  if (value) {
    let dt = value;
    if (!DateTime.isDateTime(value)) {
      dt = DateTime.fromISO(value);
    }

    if (formatString) {
      return dt.toFormat(formatString);
    }

    return dt.toFormat(UserStore.userSettings.datePattern ?? 'dd.MM.yyyy');
  }
}
